import _, { isEmpty } from "lodash";
import { createContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getCategoryItemsService,
  getOrdersService,
  getPersonnelsService,
  getServicesService,
  getUsersService,
  kpiAnalysisService,
} from "../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import { convertDate, getRandomHexColor, numberSeparate } from "../../../utils";
import { toastErrorMessage } from "../../../utils/toastMessage/toastMessage";

export const dashboardAdminContext = createContext({
  items: [],
  charts: [],
  setCharts: () => { },
  orders: [],
  setOrders: () => { },
  personnels: [],
  setPersonnels: () => { },
  users: [],
  setUsers: () => { },
  categories: [],
  setCategories: () => { },
  services: [],
  setServices: () => { },
  handleGetCategories: () => { },
  handleGetOrders: () => { },
  handleGetPersonnels: () => { },
  handleGetUsers: () => { },
  handleGetServices: () => { },
  handleGetKPIAnalysis: () => { },
  handleChartData: () => { },
  addItemToChart: () => { },
  removeItemFromChart: () => { },
  cities: [],
  setCities: () => { },
  selectedCities: [],
  setSelectedCities: () => { },
});

const DashboardAdminContext = ({ children }) => {
  const dispatch = useDispatch();
  const allCities = useSelector((state) => state.cities);

  const [charts, setCharts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [personnels, setPersonnels] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  useEffect(() => {
    if (allCities !== cities) {
      setCities(allCities);
      setSelectedCities(allCities);
    }
  }
    , [allCities])

  const items = [
    {
      title: "آمار ثبت نام کاربران",
      description: "تعداد کاربران جدیدی که ثبت نام کرده اند، به تفکیک رسانه",
      chartType: "Line",
      algorithm: "installs_count",
      items: ["android_phone", "android_web", "ios", "web", "total"],
      isDate: true,
    },
    {
      title: "آنالیز روزانه سفارشات",
      description: "تعداد سفارشات ثبت شده برای هر روز به تفکیک عادی و فولاد",
      chartType: "Line",
      nextMonth: 1,
      algorithm: "orders_count",
      items: ["foolad", "normal", "total"],
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "اولین سفارش ثبت شده هر کاربر",
      description:
        "این نمودار نوع اولین سرویسی که هر کاربر در سیستم ثبت کرده است را نمایش می‌دهد و به تحلیل رفتار اولیه کاربران کمک می کند",
      chartType: "Bar",
      algorithm: "user_first_service",
      isDate: true,
    },
    {
      title: "مدت زمان ثبت نام تا سفارش",
      description:
        "این نمودار فاصله زمانی بین ثبت نام کاربر و اولین سفارش او را بررسی می‌کند و به تحلیل سرعت تبدیل کاربران به مشتری کمک میکند.",
      chartType: "Bar",
      algorithm: "time_to_order",
      defaultPeriods: [1, 7, 30],
      isPeriods: true,
      isDate: true,
    },
    {
      title: "زمان ثبت تا خاتمه سفارش",
      description:
        "این نمودار مدت زمان بین ثبت سفارش و تکمیل یا تحویل آن را نشان می‌دهد و به تحلیل کارایی فرآیندهای پردازش و تحویل سفارشات کمک می‌کند.",
      chartType: "Bar",
      algorithm: "order_completion_time",
      defaultPeriods: [1, 7, 30],
      isPeriods: true,
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "دهک بندی کاربران بر اساس تعداد سفارشات",
      description:
        "این نمودار کاربران را بر اساس تعداد سفارشاتشان به دسته های مختلف تقسیم می‌کند و به تحلیل رفتار خرید و وفاداری مشتریان می‌پردازد.",
      chartType: "Bar",
      algorithm: "num_of_orders",
      defaultPeriods: [1, 2, 5, 10],
      isPeriods: true,
      isDate: true,
    },
    {
      title: "آنالیز رضایت کاربران",
      description: "آنالیز رضایت کاربران",
      chartType: "Bar",
      algorithm: "user_satisfaction",
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "آنالیز دلایل لغو",
      description: "آنالیز دلایل لغو",
      chartType: "Bar",
      algorithm: "cancel_reason_breakdown",
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "آنالیز دلایل کنسلی",
      description: "آنالیز دلایل کنسلی",
      chartType: "Bar",
      algorithm: "failing_reason_breakdown",
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "آنالیز دلایل تخلف",
      description: "آنالیز دلایل تخلف",
      chartType: "Bar",
      algorithm: "violation_breakdown",
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "آنالیز دلایل درخواست",
      description: "آنالیز دلایل درخواست",
      chartType: "Bar",
      algorithm: "failure_requests_breakdown",
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "آنالیز وضعیت سفارشات",
      description: "آنالیز وضعیت سفارشات",
      algorithm: "orders_status",
      items: ["daily_filtered_orders", "daily_total_orders", "percent"],
      chartType: "Line",
      isService: true,
      isServiceCategory: true,
      isDate: true,
      isStatus: true,
    },
    {
      title: "آنالیز مالی",
      description: "مجموع فاکتورها در بازه زمانی داده شده",
      algorithm: "revenue_total",
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "آنالیز مشتریان برگشتی",
      description:
        "تعداد مشتریانی که در یک بازه مشخص سفارشی نداشته اند اما مجددا فعال شده اند و سفارش ثبت کرده اند.",
      algorithm: "returning_customer",
      isPeriod: true,
      isDate: true,
    },
    {
      title: "آنالیز دهک بندی کاربران بر اساس میانگین فاکتور سفارش آنها",
      description:
        "کاربران بر اساس میانگین عدد فاکتور سفارش هایشان به دسته های متفاوتی تقسیم می شوند.",
      chartType: "Bar",
      algorithm: "average_orders",
      defaultPeriods: [0, 1_000_000, 10_000_000],
      isPeriods: true,
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "آنالیز دهک بندی کاربران بر اساس تنوع استفاده از خدمات",
      description:
        "کاربران بر اساس تنوع استفاده از خدمات متفاوت کارباما به دسته های متفاوتی تقسیم می شوند.",
      chartType: "Bar",
      algorithm: "total_services",
      defaultPeriods: [1, 2, 5, 10],
      isPeriods: true,
      isDate: true,
    },
    {
      title: "نرخ تبدیل",
      description:
        "درصد مشتریانی که سفارش ثبت کرده اند نسبت به کل مشتریان ثبت نامی",
      algorithm: "conversion_rate",
      isDate: true,
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "توزیع جغرافیایی",
      description: "توزیع جغرافیایی کاربران روی نقشه",
      algorithm: "geographic_distribution",
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "روند خدمات در طی سال",
      description: "روند کم و زیاد شدن تعداد سفارشات یک خدمت در طی سال",
      chartType: "Line",
      algorithm: "service_trend",
      isService: true,
      isServiceCategory: true,
    },
    {
      title: "پرداخت آنلاین",
      description: "این نمودار پرداخت آنلاین را بررسی می‌کند.",
      chartType: "Bar",
      algorithm: "onlinepayment_kpi",
      defaultPeriods: [1, 7, 30],
      isPeriods: true,
      isDate: true,
    },
  ];

  const handleGetCategories = async (event) => {
    event?.preventDefault();
    try {
      const { status, data } = await getCategoryItemsService();
      if (status === 200) setCategories(data);
    } catch ({ response }) {
      console.log("Categories Error");
    }
  };

  const handleGetOrders = async (event) => {
    event?.preventDefault();
    const objData = { offset: 0, limit: 10, sort_by: "-created" };
    try {
      const { status, data } = await getOrdersService(objData);
      if (status === 200) setOrders(data);
    } catch ({ response }) {
      console.log("Orders Error");
    }
  };

  const handleGetPersonnels = async (event) => {
    event?.preventDefault();
    const objData = { search: "", offset: 0, limit: 20, type: null };
    try {
      const { status, data } = await getPersonnelsService(objData);
      if (status === 200) setPersonnels(data);
    } catch ({ response }) {
      console.log("Personnel Error");
    }
  };

  const handleGetUsers = async (event) => {
    event?.preventDefault();
    const objData = { search: "", offset: 0, limit: 100, type: null };
    try {
      const { status, data } = await getUsersService(objData);
      if (status === 200) setUsers(data);
    } catch ({ response }) {
      console.log("User Error");
    }
  };

  const handleGetServices = async (event) => {
    event?.preventDefault();
    const objData = { search: "", offset: 0, limit: 300 };
    try {
      const { status, data } = await getServicesService(objData);
      if (status === 200) setServices(data);
    } catch ({ response }) {
      console.log("Service Error");
    }
  };

  const handleGetKPIAnalysis = async (objData, callBack) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await kpiAnalysisService(objData);
      if (status === 200) {
        callBack && callBack(data.result);
      }
    } catch ({ response }) {
      console.log("KPI Analysis");
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Chart Data Logic
  const handleChartData = (algorithm, data, items, title, periods) => {
    let result = {},
      convertedPeriods = [...periods, "غیره"],
      convertData = [];
    const opacity = "77";

    switch (algorithm) {
      case "installs_count":
        convertData = data?.map((_item) => ({
          ..._item,
          day: convertDate(_item.day, "DD MMM YYYY"),
        }));

        result = {
          labels: _.map(convertData, "day"),
          datasets: [],
        };

        for (const item of items) {
          const color = getRandomHexColor();
          result.datasets.push({
            label: item,
            data: _.map(convertData, item),
            fill: true,
            backgroundColor: color + opacity,
            borderColor: color,
          });
        }
        break;
      case "orders_count":
        convertData = data?.map((_item) => ({
          ..._item,
          day: convertDate(_item.day, "DD MMM YYYY"),
        }));

        result = {
          labels: _.map(convertData, "day"),
          datasets: [],
        };

        for (const item of items) {
          const color = getRandomHexColor();
          result.datasets.push({
            label: item,
            data: _.map(convertData, item),
            fill: true,
            backgroundColor: color + opacity,
            borderColor: color,
          });
        }
        break;
      case "orders_status":
        if (!isEmpty(data?.daily_filtered_orders))
          convertData = Object.keys(data?.daily_filtered_orders)?.map(
            (date) => {
              const filteredOrders = data?.daily_filtered_orders[date];
              const totalOrders = data?.daily_total_orders[date];

              return {
                day: date,
                daily_filtered_orders: filteredOrders,
                daily_total_orders: totalOrders,
                percent:
                  totalOrders === 0 ? 0 : (filteredOrders / totalOrders) * 100,
              };
            }
          );

        result = {
          labels: _.map(convertData, "day"),
          datasets: [],
        };

        for (const item of items) {
          const color = getRandomHexColor();
          result.datasets.push({
            label: item,
            data: _.map(convertData, item),
            fill: true,
            backgroundColor: color + opacity,
            borderColor: color,
          });
        }
        break;
      case "user_first_service":
        result = {
          labels: [],
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value, key) => {
          const color = getRandomHexColor();
          result.labels.push(key);
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;
      case "time_to_order":
        result = {
          labels: convertedPeriods,
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value) => {
          const color = getRandomHexColor();
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;
      case "order_completion_time":
        result = {
          labels: convertedPeriods,
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value) => {
          const color = getRandomHexColor();
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;
      case "user_satisfaction":
        result = {
          labels: [
            "بدون نظرسنجی",
            "یک ستاره",
            "دو ستاره",
            "سه ستاره",
            "چهار ستاره",
            "پنج ستاره",
          ],
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value) => {
          const color = getRandomHexColor();
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;
      case "num_of_orders":
        result = {
          labels: convertedPeriods,
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value) => {
          const color = getRandomHexColor();
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;

      case "cancel_reason_breakdown":
        result = {
          labels: [],
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value, key) => {
          const color = getRandomHexColor();
          result.labels.push(key);
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;

      case "failing_reason_breakdown":
        result = {
          labels: [],
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value, key) => {
          const color = getRandomHexColor();
          result.labels.push(key);
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;
      case "violation_breakdown":
        result = {
          labels: [],
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value, key) => {
          const color = getRandomHexColor();
          result.labels.push(key);
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;
      case "failure_requests_breakdown":
        result = {
          labels: [],
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value, key) => {
          const color = getRandomHexColor();
          result.labels.push(key);
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;

      case "average_orders":
        result = {
          labels: convertedPeriods,
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value) => {
          const color = getRandomHexColor();
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;

      case "total_services":
        result = {
          labels: convertedPeriods,
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        };

        _.map(data, (value) => {
          const color = getRandomHexColor();
          result.datasets[0].data.push(value);
          result.datasets[0].backgroundColor.push(color + opacity);
          result.datasets[0].borderColor.push(color);
        });
        break;
      case "service_trend":
        const datesArray = Object.entries(data);
        const year = new Date().getFullYear();
        const allDates = [];

        const startDate = new Date(year, 0, 1);
        const endDate = new Date(year, 11, 31);
        let counter = 0;
        while (startDate <= endDate) {
          allDates.push(datesArray[counter] ? datesArray[counter][1] : 0);
          startDate.setDate(startDate.getDate() + 1);
          counter += 1;
        }

        const startIndex = 71;
        const rearrangedData = [
          ...allDates.slice(startIndex),
          ...allDates.slice(0, startIndex),
        ];

        convertData = rearrangedData;
        const color = getRandomHexColor();
        result = {
          labels: [],
          datasets: [
            {
              label: title,
              data: [],
              backgroundColor: color + opacity,
              borderColor: color,
            },
          ],
        };

        convertData.map((value, index) => {
          let lbl = "";
          if (index < 31) lbl = "فروردین";
          else if (index < 62) lbl = "اردیبهشت";
          else if (index < 93) lbl = "خرداد";
          else if (index < 124) lbl = "تیر";
          else if (index < 155) lbl = "مرداد";
          else if (index < 186) lbl = "شهریور";
          else if (index < 216) lbl = "مهر";
          else if (index < 246) lbl = "آبان";
          else if (index < 276) lbl = "آذر";
          else if (index < 306) lbl = "دی";
          else if (index < 336) lbl = "بهمن";
          else if (index < 366) lbl = "اسفند";
          result.labels.push(lbl);
          result.datasets[0].data.push(value);
        });
        break;
      case "onlinepayment_kpi":
        const bucketLabels = periods.map((p, idx) => idx === 0 ? `تا ${p} روز` : `${periods[idx - 1]} تا ${p} روز`);

        result = {
          labels: bucketLabels,
          datasets: [
            {
              label: "مبلغ پرداخت شده",
              data: data[0], 
              backgroundColor: "#28a745" + opacity,
              borderColor: "#28a745",
            },
            {
              label: "کل مبلغ فاکتورها",
              data: data[1], 
              backgroundColor: "#007bff" + opacity,
              borderColor: "#007bff",
            }
          ]
        };
        break;
      default:
        break;
    }
    return result;
  };

  const addItemToChart = (algorithm) => {
    const item = items.find((_i) => _i.algorithm === algorithm);

    if (isEmpty(item)) return toastErrorMessage("الگوریتم یافت نشد");

    setCharts((prev) => [...prev, { ...item, id: Math.random() }]);
  };

  const removeItemFromChart = (index) =>
    setCharts((prev) => prev.filter((_, idx) => idx !== index));

  return (
    <dashboardAdminContext.Provider
      value={{
        items,
        charts,
        setCharts,
        orders,
        setOrders,
        personnels,
        setPersonnels,
        categories,
        setCategories,
        users,
        setUsers,
        services,
        setServices,
        handleGetCategories,
        handleGetOrders,
        handleGetPersonnels,
        handleGetUsers,
        handleGetServices,
        handleGetKPIAnalysis,
        handleChartData,
        addItemToChart,
        removeItemFromChart,
        cities,
        selectedCities,
        setSelectedCities,
      }}
    >
      {children}
    </dashboardAdminContext.Provider>
  );
};

export default DashboardAdminContext;